import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Autocomplete,
  Typography,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { 
  fetchPersonMonths, 
  createPersonMonth, 
  updatePersonMonth, 
  deletePersonMonth 
} from 'shared/utils/proposalApi';
import { useSnackbar } from 'shared/contexts/SnackbarContext';

const PersonMonthDialog = ({ open, onClose, personMonthItem, task, onSuccess, consortiumMembers = [] }) => {
  const { showSnackbar } = useSnackbar();
  const [, setPersonMonths] = useState([]);
  const [formData, setFormData] = useState({
    user: null,
    months: ''
  });
  const [, setEditingIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (open && task?.id) {
      loadPersonMonths();
    } else if (open) {
      setPersonMonths([]);
    }
  }, [open, task]);

  useEffect(() => {
    // If editing an existing personMonthItem, pre-fill the form
    if (personMonthItem && open) {
      const user = consortiumMembers.find(u => u.id === personMonthItem.user)
               || (task?.assignee_names || []).find(u => u.id === personMonthItem.user)
               || null;
      setFormData({
        user: user,
        months: personMonthItem.months?.toString() || ''
      });
      setEditingIndex(null); // This indicates we're editing an existing item
    } else if (open) {
      // Reset form for new entry
      setFormData({ user: null, months: '' });
      setEditingIndex(null);
    }
  }, [personMonthItem, open, consortiumMembers]);

  const loadPersonMonths = async () => {
    setLoading(true);
    try {
      const data = await fetchPersonMonths(task.id);
      console.log('Loaded person months:', data);
      if (data && data.person_months) {
        setPersonMonths(data.person_months);
      }
    } catch (err) {
      console.error('Error loading person months:', err);
      showSnackbar('Failed to load person months');
      setError('Failed to load person months');
    }
    setLoading(false);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSubmit = async () => {
    if (!formData.user || !formData.months) {
      const errorMsg = 'Please select a user and enter months';
      setError(errorMsg);
      showSnackbar(errorMsg);
      return;
    }

    const monthsValue = parseFloat(formData.months);
    if (isNaN(monthsValue) || monthsValue <= 0) {
      const errorMsg = 'Please enter a valid positive number for months';
      setError(errorMsg);
      showSnackbar(errorMsg);
      return;
    }

    setIsSaving(true);
    setError(null);

    const submitData = {
      task: task.id,
      user: formData.user.id,
      months: monthsValue
    };

    try {
      let result;
      if (personMonthItem?.id) {
        // Editing existing item
        result = await updatePersonMonth(personMonthItem.id, submitData);
      } else {
        // Creating new item
        result = await createPersonMonth(submitData);
      }
      
      if (result.success) {
        showSnackbar(personMonthItem?.id ? 'Person month updated successfully' : 'Person month added successfully');
        
        // Refresh the list
        await loadPersonMonths();
        
        // Notify parent component
        onSuccess && onSuccess();
        
        // Close the dialog
        onClose();
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : JSON.stringify(result.error);
        setError(errorMsg);
        showSnackbar(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to save person month';
      setError(errorMsg);
      showSnackbar(errorMsg);
    }

    setIsSaving(false);
  };

  const handleDelete = async () => {
    if (!personMonthItem?.id) {
      const errorMsg = 'No person month item selected for deletion';
      setError(errorMsg);
      showSnackbar(errorMsg);
      return;
    }

    if (!window.confirm('Are you sure you want to delete this person month allocation?')) {
      return;
    }

    setIsDeleting(true);
    setError(null);

    try {
      const result = await deletePersonMonth(personMonthItem.id);
      
      if (result.success) {
        showSnackbar('Person month deleted successfully');
        
        // Refresh the list
        await loadPersonMonths();
        
        // Notify parent component
        onSuccess && onSuccess();
        
        // Close the dialog
        onClose();
      } else {
        const errorMsg = typeof result.error === 'string' ? result.error : JSON.stringify(result.error);
        setError(errorMsg);
        showSnackbar(errorMsg);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to delete person month';
      setError(errorMsg);
      showSnackbar(errorMsg);
    }

    setIsDeleting(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 3000 }}>
      <DialogTitle>
        {personMonthItem ? 'Edit Person Month' : 'Add Person Month'} - {task?.title || 'Task'}
        {personMonthItem && (
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={handleDelete}
              disabled={isDeleting}
              color="error"
              sx={{ 
                position: 'absolute', 
                right: 8, 
                top: 8 
              }}
            >
              {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            </IconButton>
          </Tooltip>
        )}
      </DialogTitle>
      <DialogContent>
        {/* <Stack spacing={3} sx={{ mt: 1 }}> */}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (

// border: '1px solid', borderColor: 'divider', 
            <Box sx={{ mt: 1 }}>
              {/* <Typography variant="subtitle2" gutterBottom>
                {personMonthItem ? 'Edit Person Month Allocation' : 'Add New Person Month Allocation'}
              </Typography> */}
              
              <Stack direction="row" spacing={2}>
                <Autocomplete
                  options={consortiumMembers}
                  value={formData.user}
                  fullWidth
                  slotProps={{ popper: { style: { zIndex: 3001 } } }}
                 
                  onChange={(e, newValue) => handleChange('user', newValue)}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    const name = option.name || option.email || option.username || 'Unknown';
                    console.log('##### Option in getOptionLabel:', option);
                    return option.org_short_name ? `${name} (${option.org_short_name})` : name;
                  }}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contributor"
                      required
                      size="small"
                      helperText="Must be a task assignee"
                    />
                  )}
                  disableClearable={false}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      if (!option) return null;
                      return (
                        <Chip
                          key={option.id || index}
                          label={`${option.name || option.email || 'Unknown'}`}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      );
                    })
                  }
                />
                
                <TextField
                  label="Person Months"
                  type="number"
                  value={formData.months}
                  onChange={(e) => handleChange('months', e.target.value)}
                  required
                  size="small"
                  fullWidth
                  sx={{ width: '30%' }}
                  InputProps={{ 
                    inputProps: { 
                      min: 0, 
                      step: 0.1,
                      max: 12
                    } 
                  }}
                  // helperText="Enter number of person months (e.g., 2.5)"
                />
                  </Stack>
                <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ pt: 2 }}>
                  <Box>
                    {personMonthItem && (
                      <Button
                        size="small"
                        color="error"
                        variant="outlined"
                        onClick={handleDelete}
                        disabled={isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={16} /> : <DeleteIcon />}
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </Button>
                    )}
                  </Box>
                  
                  <Stack direction="row" spacing={1}>
                    <Button size="small" onClick={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={isSaving || !formData.user || !formData.months}
                    >
                      {isSaving ? (
                        <CircularProgress size={20} />
                      ) : (
                        personMonthItem ? 'Update' : 'Add'
                      )}
                    </Button>
                
                </Stack>
              </Stack>
            </Box>
          )}
        {/* </Stack> */}
      </DialogContent>
      {/* <DialogActions>
        <Button onClick={onClose}>
          Close
        </Button>
      </DialogActions> */}
    </Dialog>
  );
};

export default PersonMonthDialog;